<template>
  <v-dialog :model-value="modelValue" @update:model-value="onClose" max-width="600">
    <v-card>
      <v-card-title>Share "{{ agentName }}"</v-card-title>
      <v-card-text>
        <!-- <v-alert types -->

        <v-text-field
          v-model="recipient"
          label="Username or Email"
          :disabled="loading"
          @keyup.enter="addShare"
          clearable
        />
        <div class="d-flex align-center">
          <v-checkbox
            v-model="editable"
            label="Allow editing"
            hide-details
            density="compact"
            :disabled="loading"
            class="mr-3 my-0 py-0 flex-grow-0"
          />
          <v-btn color="primary" :disabled="loading || !recipient.trim()" @click="addShare">
            <v-progress-circular
              v-if="loading"
              indeterminate
              size="16"
              width="2"
              color="white"
              class="mr-2"
            />
            <span>{{ loading ? 'Sharing...' : 'Share' }}</span>
          </v-btn>
        </div>

        <v-divider class="my-4" />

        <div v-if="shares.length === 0" class="text-medium-emphasis">No recipients yet.</div>
        <v-list v-else density="compact">
          <v-list-item
            v-for="(s, i) in shares"
            :key="i"
            :title="`${s.recipientType}: ${s.recipient}`"
            :subtitle="new Date(s.createdAt).toLocaleString()"
          >
            <template #append>
              <v-switch
                :model-value="s.editable"
                @update:model-value="(v) => setEditable(s, v === true)"
                label="Editable"
                color="primary"
                hide-details
                density="compact"
                :disabled="loading"
                class="mr-2 my-0 py-0"
              />
              <v-btn
                icon="mdi-delete-outline"
                variant="text"
                @click="revoke(s)"
                :disabled="loading"
                :aria-label="`Revoke ${s.recipientType} ${s.recipient}`"
              />
            </template>
          </v-list-item>
        </v-list>

        <div v-if="error" class="text-error mt-2">{{ error }}</div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="onClose(false)">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import type { AgentShareDTO } from '@/types/public'
import { listPublishedAgentShares, sharePublishedAgent, revokePublishedAgentShare } from '@/utils/api'
import { isApiError } from '@/types/api'

@Options({
  props: {
    modelValue: { type: Boolean, required: true },
    ownerUsername: { type: String, required: true },
    slug: { type: String, required: true },
    agentName: { type: String, required: true }
  },
  emits: ['update:modelValue'],
  watch: {
    async modelValue(this: AgentShareDialog, open: boolean) {
      if (open) {
        await this.loadShares()
      }
    }
  }
})
export default class AgentShareDialog extends Vue {
  // Props
  modelValue!: boolean
  ownerUsername!: string
  slug!: string
  agentName!: string

  // Local state
  recipient: string = ''
  editable: boolean = false
  shares: AgentShareDTO[] = []
  loading: boolean = false
  error: string = ''

  async loadShares(): Promise<void> {
    this.loading = true
    this.error = ''
    const res = await listPublishedAgentShares(this.ownerUsername, this.slug)
    this.loading = false
    if (isApiError(res)) {
      this.error = res.message || 'Failed to load shares'
      this.shares = []
    } else if (res.success) {
      this.shares = res.data ?? []
    } else {
      this.error = 'Failed to load shares'
    }
  }

  async addShare(): Promise<void> {
    const r = this.recipient.trim()
    if (!r) return
    this.loading = true
    this.error = ''
    const res = await sharePublishedAgent(this.ownerUsername, this.slug, r, this.editable)
    this.loading = false
    if (isApiError(res) || !res.success) {
      const err = (isApiError(res) ? res.message : 'Failed to share') || 'Failed to share'
      this.error = err
      this.$store.dispatch('updateSnackbar', {
        text: err,
        color: 'error',
        timeout: 5000
      })
    } else {
      this.recipient = ''
      await this.loadShares()
      this.$store.dispatch('updateSnackbar', {
        text: `Agent shared with ${r}.`,
        color: 'success',
        timeout: 3000
      })
    }
  }

  async setEditable(s: AgentShareDTO, v: boolean): Promise<void> {
    this.loading = true
    this.error = ''
    const res = await sharePublishedAgent(this.ownerUsername, this.slug, s.recipient, v)
    this.loading = false
    if (isApiError(res) || !res.success) {
      const err = (isApiError(res) ? res.message : 'Failed to update editable') || 'Failed to update editable'
      this.error = err
      this.$store.dispatch('updateSnackbar', { text: err, color: 'error', timeout: 5000 })
      return
    }
    await this.loadShares()
    this.$store.dispatch('updateSnackbar', {
      text: `${s.recipientType}: ${s.recipient} ${v ? 'can now edit' : 'is now read-only'}.`,
      color: 'success',
      timeout: 3000
    })
  }

  async revoke(s: AgentShareDTO): Promise<void> {
    this.loading = true
    this.error = ''
    const res = await revokePublishedAgentShare(this.ownerUsername, this.slug, s.recipientType, s.recipient)
    this.loading = false
    if (isApiError(res) || !res.success) {
      const err = (isApiError(res) ? res.message : 'Failed to revoke') || 'Failed to revoke'
      this.error = err
      this.$store.dispatch('updateSnackbar', {
        text: err,
        color: 'error',
        timeout: 5000
      })
    } else {
      await this.loadShares()
      this.$store.dispatch('updateSnackbar', {
        text: `Access revoked for ${s.recipientType}: ${s.recipient}.`,
        color: 'success',
        timeout: 3000
      })
    }
  }

  onClose(v: boolean): void {
    this.$emit('update:modelValue', v)
  }
}
</script>
